import React from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import { useUser } from '@paintscout/ui-business'
import { useInternalStaff } from 'src/hooks/useInternalStaff'

/**
 * Guard component that blocks an inactive company out of the app.
 *
 * Fires for any company that is not subscribed (`!isSubscribed`) — this covers the strict churned
 * case (cancelled + term ended) as well as a company simply flagged inactive (e.g.
 * `company.active === false` set by the Chargebee cancellation webhook, regardless of subscription
 * status or term end). nonBilling companies and active trials are subscribed, so they pass through
 * untouched; trial-expired users are handled separately by TrialExpiredGuard.
 *
 * This is the ONLY route-level block for an unsubscribed account. Everywhere else that reads
 * `isSubscribed` only disables an individual control. Remove this and a cancelled account can
 * still browse and edit everything — the block screen would only appear if they happened to land
 * on /dashboard.
 *
 * There is deliberately no polling here (ENG-1532). Two timers used to run:
 *
 *   - a 5-minute `authenticate({ ignoreCache: true })` from #22013, meant to catch a mid-session
 *     cancellation. It never could: `isSubscribed` comes from `appDocs`' `companyDetails`, not the
 *     token (ENG-1533). It was a rotating refresh-token exchange every 5 minutes per session, for
 *     nothing — the same grant churn `AuthorizeUser.tsx` goes out of its way to avoid after
 *     ENG-1190. Auth0 refreshes on demand (`useRefreshTokens`), and permission changes have their
 *     own event-driven path in AuthorizeUser.
 *   - an `appDocs` refetch for past-due sessions, to clear the banner after an out-of-band
 *     payment. Every other route to that already self-heals: paying in the dialog refetches
 *     appDocs, reloading re-runs it on mount, and opening the dialog makes `outstandingInvoice`
 *     close a stale episode server-side. The only gap left is a cosmetic stale banner for someone
 *     who paid from a Chargebee email and never reloaded — not worth a timer.
 *
 * So account state updates on page load and new tabs, never mid-session. That is a deliberate
 * trade, not an oversight.
 */
export default function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  const { isSubscribed } = useUser()
  const isInternalStaff = useInternalStaff()
  const location = useLocation()

  // Allow access to settings/billing routes when inactive
  const isBillingRoute = location.pathname.startsWith('/settings')
  // ...and to /dashboard itself, which is where this guard sends people. Without this the
  // redirect fires on its own destination: /dashboard renders <Redirect to="/dashboard" />, which
  // re-runs the guard, which redirects again. The children never render and the block screen
  // shows as a blank page under the header.
  const isBlockScreenRoute = location.pathname.startsWith('/dashboard')

  // If the company is inactive and not on a billing route, send them to the block screen
  // (ENG-1532) — /dashboard renders DeactivatedDashboard for an unsubscribed account. It explains
  // what happened and offers Select Plan, where the old redirect dropped the user straight into
  // the billing settings page with no explanation.
  //
  // This is also what a quote-notification deep link lands on: notification emails keep going out
  // while an account is deactivated (the issue is explicit that they must), and their
  // /quotes/edit/:id links now arrive here instead of at a broken editor.
  //
  // /settings still passes through so Select Plan and RenewPortal work. Superadmin and support
  // bypass the redirect so internal staff can still reach the rest of the app to investigate —
  // note that /dashboard itself shows them the block screen anyway, since the normal dashboard
  // renders empty for an unsubscribed account.
  if (!isSubscribed && !isBillingRoute && !isBlockScreenRoute && !isInternalStaff) {
    return <Redirect to="/dashboard" />
  }

  return <>{children}</>
}
